import { useEffect, useId, useRef, useState } from 'react';
import { Info, X } from 'lucide-react';
import { NotesToolDetailsSchema, type NotesToolDetails as Details } from '../../shared/notes-details.ts';
import { api, ApiError } from './api.ts';
import { Button } from '@/components/ui/button.tsx';
import { Alert, AlertDescription } from '@/components/ui/alert.tsx';

export function NotesToolDetails({representativeId,active}:{representativeId:string;active:boolean}) {
  const [open,setOpen]=useState(false);
  const [details,setDetails]=useState<Details|null>(null);
  const [error,setError]=useState<string|null>(null);
  const [attempt,setAttempt]=useState(0);
  const trigger=useRef<HTMLButtonElement>(null);
  const id=useId();
  useEffect(()=>{
    if(!active) {setOpen(false);setDetails(null);setError(null);}
  },[active]);
  useEffect(()=>{
    setDetails(null);setError(null);
    if(!open || !active) return;
    const controller=new AbortController();
    void api<{details:unknown}>('/api/representative/notes/details',{avatarId:representativeId,signal:controller.signal})
      .then(result=>{
        const parsed=NotesToolDetailsSchema.parse(result.details);
        if(parsed.responsibleEmployee.id!==representativeId) throw Error('Representative changed');
        if(!controller.signal.aborted) setDetails(parsed);
      })
      .catch(error=>{
        if(!controller.signal.aborted) setError(error instanceof ApiError && error.status===401
          ? 'Your sign-in expired. Sign in again to view tool details.'
          : 'Tool details are unavailable. Your notes have not changed.');
      });
    return ()=>controller.abort();
  },[open,active,representativeId,attempt]);
  const close=()=>{setOpen(false);setDetails(null);setError(null);trigger.current?.focus();};
  return <div className="space-y-3">
    <Button ref={trigger} variant="ghost" size="sm" aria-expanded={open && active} aria-controls={id}
      onClick={()=>{setDetails(null);setError(null);setOpen(value=>!value);}}>
      <Info aria-hidden="true"/> Tool details
    </Button>
    {open && active && <section id={id} aria-label="Notes tool details" className="rounded-xl border border-border bg-card p-4 sm:p-5">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h3 className="font-medium">Private Notes</h3>
        <Button variant="ghost" size="icon-sm" aria-label="Close tool details" onClick={close}><X aria-hidden="true"/></Button>
      </div>
      {error ? <Alert variant="destructive"><AlertDescription className="space-y-2">
        <p role="alert">{error}</p>
        <Button variant="outline" size="sm" onClick={()=>{setError(null);setAttempt(value=>value+1);}}>Retry details</Button>
      </AlertDescription></Alert> : !details ? <p role="status" className="text-sm text-muted-foreground">Loading tool details...</p> : <>
        <dl className="grid gap-4 text-sm sm:grid-cols-2">
          <div className="min-w-0"><dt className="text-muted-foreground">Workspace</dt><dd className="mt-1 break-words font-medium">{details.workspace.name}</dd></div>
          <div className="min-w-0"><dt className="text-muted-foreground">Responsible employee</dt><dd className="mt-1 break-words font-medium">{details.responsibleEmployee.name} - proposed owner</dd></div>
          <div><dt className="text-muted-foreground">Privacy</dt><dd className="mt-1">Private to you in this workspace.</dd></div>
          <div><dt className="text-muted-foreground">Employee access</dt><dd className="mt-1">{details.responsibleEmployee.name} currently helps with your notes. Other employees have no access through this tool.</dd></div>
        </dl>
        <div className="mt-5 space-y-1 border-t border-border pt-4 text-sm">
          <p className="font-medium">Marketplace setup not activated</p>
          <p className="text-muted-foreground">Marketplace adoption has not been verified. Your current Notes tool works as usual; this preview adds no permissions.</p>
        </div>
      </>}
    </section>}
  </div>;
}
